// A message's push and its realtime event land a few seconds apart, which is all there is to
// match them on when the push comes from an api that does not send the message id.
export const MESSAGING_TOAST_DEDUPE_WINDOW_MS = 10_000;

// A message's push and realtime event arrive seconds apart, so the last 500 toasted ids are far
// more history than a match ever needs, and the set stays small over a long shift.
export const MESSAGING_TOAST_REMEMBERED_MESSAGE_IDS_LIMIT = 500;

export type MessagingToastSource = 'push' | 'realtime';

type MessagingToast = {
  channelId: string;
  messageId: string | null;
  source: MessagingToastSource;
};

export const createMessagingToastDedupe = () => {
  const lastToastByChannelId = new Map<
    string,
    { messageId: string | null; source: MessagingToastSource; toastedAt: number }
  >();
  const toastedMessageIds = new Set<string>();

  const isRepeatOfEarlierToast = ({
    channelId,
    isToastShowing,
    messageId,
    source,
  }: MessagingToast & { isToastShowing: boolean }) => {
    const lastToast = lastToastByChannelId.get(channelId);
    const canOnlyMatchByTimeWindow = messageId === null || lastToast?.messageId === null;
    // Without a message id, a push and a realtime event for one conversation inside the
    // window are taken to be the same message.
    const isSameMessageByWindow =
      canOnlyMatchByTimeWindow &&
      lastToast !== undefined &&
      lastToast.source !== source &&
      Date.now() - lastToast.toastedAt < MESSAGING_TOAST_DEDUPE_WINDOW_MS;
    const isMessageAlreadyToasted =
      messageId !== null && toastedMessageIds.has(messageId);
    const isToastShowingPushForThisMessage =
      isToastShowing &&
      lastToast?.source === 'push' &&
      (lastToast.messageId === null || lastToast.messageId === messageId);
    const isRefreshingPushToast =
      source === 'realtime' && isToastShowingPushForThisMessage;
    const isSameMessageAsEarlierToast = isSameMessageByWindow || isMessageAlreadyToasted;

    return isSameMessageAsEarlierToast && !isRefreshingPushToast;
  };

  const recordToast = ({ channelId, messageId, source }: MessagingToast) => {
    lastToastByChannelId.set(channelId, { messageId, source, toastedAt: Date.now() });
    if (messageId === null) return;

    toastedMessageIds.add(messageId);
    const isOverLimit =
      toastedMessageIds.size > MESSAGING_TOAST_REMEMBERED_MESSAGE_IDS_LIMIT;
    const oldestMessageId = toastedMessageIds.values().next().value;
    if (isOverLimit && oldestMessageId !== undefined) {
      toastedMessageIds.delete(oldestMessageId);
    }
  };

  return { isRepeatOfEarlierToast, recordToast };
};
